import React, { useEffect } from 'react';
import { CancellationSummary as Summary } from 'guest-data-types-ts-lib';
import { Button, CancellationCharges, RefundDetails } from '../../components';
import './CancellationSummary.scss';
import { CurrencyUtils, TranslationUtils } from '../../utils';
import { Configuration } from '../../configuration';
import { setReservationTripProtectionInfo } from '../../store/actions';
import { useDispatch } from 'react-redux';

interface CancellationSummaryProps {
    summary: Summary;
    onConfirmCancel: () => void;
}

export const CancellationSummary: React.FC<CancellationSummaryProps> = (props) => {
    const { summary, onConfirmCancel } = props;
    const dispatch = useDispatch();
    const isFullRefund = summary.type == 'FULL_REFUND';
    const isNoFundsCollectedWithPayments = summary.type == 'NO_FUNDS_COLLECTED' && summary.paid.total > 0;
    const shouldShowBreakdown = ['FULL_REFUND', 'PARTIAL_REFUND'].includes(summary.type) || isNoFundsCollectedWithPayments;

    useEffect(() => {
        dispatch(setReservationTripProtectionInfo(summary.cancellation_instructions_extended || ''));
    }, [summary]);

    const getCTAOptions = (summary: Summary) => {
        let confirmCancelLabel = TranslationUtils.get('cancel_trip');
        let shouldShowCancelButton = true;

        const viewPolicyProps = {
            label: TranslationUtils.get('view_cancellation_policy'),
            onClick: () => {
                const url = Configuration.getCancellationPolicyURL();
                window.open(url, '_blank');
            },
        };

        if (['NOT_ALLOWED', 'EXTERNAL'].includes(summary.type)) {
            shouldShowCancelButton = false;
        }

        if (['FULL_REFUND', 'PARTIAL_REFUND'].includes(summary.type)) {
            confirmCancelLabel = TranslationUtils.get('cancel_and_receive_refund');
        }

        if (summary.type == 'EXTERNAL' && summary.external_instructions && summary.external_instructions.url) {
            viewPolicyProps.label = summary.external_instructions.overview;
            const externalUrl = summary.external_instructions.url;
            viewPolicyProps.onClick = () => {
                window.open(externalUrl, '_blank');
            };
        }

        return (
            <div className="cancellation-action-buttons">
                <Button className={'body-regular'} block variant="info" {...viewPolicyProps} />
                {shouldShowCancelButton && (
                    <Button className={'body-regular'} block variant="primary" label={confirmCancelLabel} onClick={onConfirmCancel} />
                )}
            </div>
        );
    };

    return (
        <div id={'main-cancellation-summary'}>
            <p id={'cancellation-summary-overview'} className="body-regular cancellation-summary-overview">
                <div dangerouslySetInnerHTML={{ __html: summary.cancellation_overview }} />
            </p>

            {shouldShowBreakdown && (
                <React.Fragment>
                    <div className="cancellation-summary-original-payment">
                        <p className="body-xl cancellation-summary-original-payment-title">{TranslationUtils.get('original_payment')}</p>
                        <hr className="cancellation-summary-original-payment-divider" />
                        <div className="body-bold cancellation-summary-original-payment-total">
                            {CurrencyUtils.formatCurrency(+summary.paid.total, summary.paid.currency || 'USD')}
                        </div>
                    </div>

                    {!isFullRefund && summary.charges && <CancellationCharges charges={summary.charges} />}
                </React.Fragment>
            )}

            {shouldShowBreakdown && summary.refund.total_refund_amount.total > 0 && (
                <React.Fragment>
                    <RefundDetails className="cancellation-summary-refund-details" refund={summary.refund} />
                    <div className="cancellation-summary-total-refund">
                        <span className={'heading-small'}>
                            {CurrencyUtils.formatCurrency(
                                summary.refund.total_refund_amount.total,
                                summary.refund.total_refund_amount.currency || 'USD'
                            )}
                        </span>
                        <span className={'body-small'}>{TranslationUtils.get('total_refund')}</span>
                    </div>
                </React.Fragment>
            )}

            {getCTAOptions(summary)}
        </div>
    );
};
